import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Badge } from './Badge';
import { FindingSeverity, FindingStatus } from '../../types/finding';
import { SEVERITY_LABEL, FINDING_STATUS_TONE, FINDING_STATUS_LABEL } from '../severity';

/**
 * Kolega design-system — FindingCard
 * The core list row: a security finding with severity + status pills, CVSS,
 * optional PR marker, title and source path. Composes Badge. The compact
 * layout shows a left severity rail and a trailing chevron.
 */
const SEV_VAR: Record<FindingSeverity, string> = {
  critical: 'var(--sev-critical)',
  high: 'var(--sev-high)',
  medium: 'var(--sev-medium)',
  low: 'var(--sev-low)',
};

export interface FindingCardProps {
  title: string;
  severity?: FindingSeverity;
  status?: FindingStatus;
  cvss?: string | number;
  file?: string;
  hasPR?: boolean;
  prLabel?: string;
  compact?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export function FindingCard({
  title,
  severity = 'high',
  status = 'open',
  cvss,
  file,
  hasPR = false,
  prLabel = 'PR Open',
  compact = false,
  onClick,
  style,
}: FindingCardProps) {
  const sevColor = SEV_VAR[severity] || SEV_VAR.low;
  const border = hasPR
    ? 'var(--purple-border)'
    : status === 'resolved'
    ? 'rgba(74,222,128,0.22)'
    : 'var(--border-subtle)';

  if (compact) {
    return (
      <div
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          background: 'var(--surface-1)',
          border: `1px solid ${border}`,
          borderRadius: 'var(--radius-lg)',
          padding: '15px 18px',
          cursor: onClick ? 'pointer' : 'default',
          fontFamily: 'var(--font-sans)',
          ...style,
        }}
      >
        <div style={{ width: 5, height: 42, borderRadius: 3, background: sevColor, flex: 'none' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 14.5, color: 'var(--text-primary)' }}>{title}</span>
            <Badge tone={severity}>{SEVERITY_LABEL[severity]}</Badge>
            {cvss != null && cvss !== '' ? (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>CVSS {cvss}</span>
            ) : null}
            {hasPR ? <Badge tone="pr">{prLabel}</Badge> : null}
          </div>
          {file ? (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {file}
            </div>
          ) : null}
        </div>
        <Badge tone={FINDING_STATUS_TONE[status]}>{FINDING_STATUS_LABEL[status]}</Badge>
        <ChevronRight style={{ width: 16, height: 16, color: 'var(--text-faint)', flex: 'none' }} />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface-1)',
        border: `1px solid ${border}`,
        borderRadius: 'var(--radius-lg)',
        padding: '16px 18px',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
        <Badge tone={severity}>{SEVERITY_LABEL[severity]}</Badge>
        <Badge tone={FINDING_STATUS_TONE[status]}>{FINDING_STATUS_LABEL[status]}</Badge>
        {cvss != null && cvss !== '' ? (
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-mono)',
              background: 'var(--surface-3)',
              padding: '2px 7px',
              borderRadius: 'var(--radius-xs)',
            }}
          >
            CVSS {cvss}
          </span>
        ) : null}
        {hasPR ? <Badge tone="pr">{prLabel}</Badge> : null}
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', marginBottom: 4 }}>{title}</div>
      {file ? (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{file}</div>
      ) : null}
    </div>
  );
}

export default FindingCard;
